// src/pages/ParametresPaiement.js - Configuration admin des passerelles NITA / AMANA
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
};

// Les lignes 'nita' et 'amana' sont créées par la migration
// 2026_07_10_passerelles.sql — on ne fait qu'éditer leur config_json ici,
// jamais de création (gateway_update.php fait un INSERT ... ON DUPLICATE KEY
// UPDATE côté serveur donc ça marche même si la ligne n'existait pas encore).
const GATEWAYS = [
  {
    code: 'nita',
    label: 'NITA',
    logo: '/assets/img/mynita_logo.png',
    color: '#F5871F',
    champs: [
      { key: 'base_url', label: "URL de l'API (fournie par NITA)", placeholder: 'https://api.nita.example.com' },
      { key: 'api_key', label: 'Clé API' },
      { key: 'username', label: 'Identifiant partenaire' },
      { key: 'password', label: 'Mot de passe partenaire' },
    ],
  },
  {
    code: 'amana',
    label: 'AMANA',
    logo: '/assets/img/amana_logo.png',
    color: '#1E9E5A',
    champs: [
      { key: 'base_url', label: "URL de l'API (fournie par AMANA)", placeholder: 'https://api.amana.example.com' },
      { key: 'api_key', label: 'Clé API' },
      { key: 'userlogin', label: 'Login B2B' },
      { key: 'userpass', label: 'Mot de passe B2B' },
    ],
  },
];

const GatewayCard = ({ gateway, data, onSave, saving }) => {
  const [nomAffichage, setNomAffichage] = useState(data?.nom_affichage || gateway.label);
  const [actif, setActif] = useState(!!data?.actif);
  const [values, setValues] = useState({});
  const [showSecrets, setShowSecrets] = useState({});

  useEffect(() => {
    setNomAffichage(data?.nom_affichage || gateway.label);
    setActif(!!data?.actif);
    setValues({});
  }, [data, gateway.label]);

  const webhookUrl = `${API_BASE_URL}/paiements/${gateway.code}_webhook.php`;

  const handleCopyWebhook = () => {
    navigator.clipboard?.writeText(webhookUrl);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // On n'envoie que les champs réellement modifiés : un champ laissé vide
    // conserve sa valeur précédente côté serveur (voir sauvegarder_gateway()).
    const config = {};
    gateway.champs.forEach(({ key }) => {
      if (values[key]) config[key] = values[key];
    });
    onSave(gateway.code, { nom_affichage: nomAffichage, actif, mode: 'api', config });
  };

  return (
    <div className="col-lg-6 mb-4">
      <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', borderTop: `4px solid ${gateway.color}` }}>
        <div className="card-body p-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{
                width: '44px', height: '44px', backgroundColor: `${gateway.color}18`, overflow: 'hidden'
              }}>
                <img src={gateway.logo} alt={gateway.label} style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
              </div>
              <h5 className="mb-0" style={{ color: COLORS.primary, fontWeight: '700' }}>{gateway.label}</h5>
            </div>
            <span className="badge" style={{
              backgroundColor: actif ? '#e8f5e9' : '#f5f5f5',
              color: actif ? '#2e7d32' : '#757575',
              padding: '5px 12px', borderRadius: '20px', fontWeight: '600'
            }}>
              {actif ? 'Actif' : 'Inactif'}
            </span>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.85rem' }}>
                URL de webhook à fournir à {gateway.label}
              </label>
              <div className="input-group">
                <input type="text" className="form-control" value={webhookUrl} readOnly
                  style={{ backgroundColor: COLORS.lightBg, color: COLORS.darkText, fontSize: '0.85rem' }} />
                <button type="button" className="btn" onClick={handleCopyWebhook}
                  style={{ borderColor: '#e0e0e0', color: COLORS.secondary }} title="Copier">
                  <i className="bi bi-clipboard"></i>
                </button>
              </div>
              <div className="form-text" style={{ fontSize: '0.75rem' }}>
                Communiquez cette URL à {gateway.label} pour qu'ils vous notifient des paiements.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.85rem' }}>
                Nom affiché aux gestionnaires
              </label>
              <input type="text" className="form-control" value={nomAffichage}
                onChange={(e) => setNomAffichage(e.target.value)}
                style={{ borderColor: '#e0e0e0' }} />
            </div>

            {gateway.champs.map(({ key, label, placeholder }) => (
              <div className="mb-3" key={key}>
                <label className="form-label" style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.85rem' }}>
                  {label}
                </label>
                <div className="input-group">
                  <input
                    type={showSecrets[key] ? 'text' : 'password'}
                    className="form-control"
                    value={values[key] || ''}
                    onChange={(e) => setValues(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={
                      data?.config?.[key]
                        ? `${data.config[key]}  (laisser vide pour ne pas modifier)`
                        : (placeholder || 'Non configuré')
                    }
                    style={{ borderColor: '#e0e0e0' }}
                    autoComplete="off"
                  />
                  <button type="button" className="btn" style={{ borderColor: '#e0e0e0', color: COLORS.secondary }}
                    onClick={() => setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }))}
                    title={showSecrets[key] ? 'Masquer' : 'Afficher'}>
                    <i className={`bi ${showSecrets[key] ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                  </button>
                </div>
              </div>
            ))}

            <div className="form-check form-switch mb-4">
              <input className="form-check-input" type="checkbox" role="switch" id={`actif-${gateway.code}`}
                checked={actif} onChange={(e) => setActif(e.target.checked)} />
              <label className="form-check-label" htmlFor={`actif-${gateway.code}`} style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                Activer ce moyen de paiement pour les clients
              </label>
            </div>

            <button type="submit" className="btn w-100 fw-semibold" disabled={saving}
              style={{ backgroundColor: gateway.color, borderColor: gateway.color, color: 'white', padding: '10px', borderRadius: '8px' }}>
              {saving ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : (
                <i className="bi bi-download me-2"></i>
              )}
              Enregistrer {gateway.label}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const ParametresPaiement = () => {
  const [passerelles, setPasserelles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [savingCode, setSavingCode] = useState(null);

  // Le backend (paiements/gateway_list.php et gateway_update.php) refuse déjà
  // tout non-admin — ce garde côté page est un renfort défense-en-profondeur
  // pour ne pas laisser un non-admin arriver sur une page vide/en erreur.
  let currentProfile = 0;
  try {
    currentProfile = Number(JSON.parse(localStorage.getItem("user") || "{}")?.profile || 0);
  } catch (e) { /* ignore */ }
  const isAdmin = currentProfile === 1;

  const fetchGateways = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API_BASE_URL}/paiements/gateway_list.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        const map = {};
        (res.data.passerelles || []).forEach(p => { map[p.code] = p; });
        setPasserelles(map);
      } else {
        setError(res.data?.message || "Erreur lors du chargement.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Impossible de charger les passerelles de paiement.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchGateways();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchGateways]);

  if (!isAdmin) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '700', fontSize: '1.8rem' }}>
            <i className="bi bi-credit-card me-2"></i>
            Paramètres de paiement
          </h1>
        </div>
        <section className="section">
          <div className="alert alert-warning">
            Accès réservé à l'administrateur.
          </div>
        </section>
      </Layout>
    );
  }

  const handleSave = async (code, payload) => {
    setSavingCode(code);
    setError("");
    setSuccessMsg("");
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(
        `${API_BASE_URL}/paiements/gateway_update.php`,
        { code, ...payload },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (res.data?.success) {
        setSuccessMsg(`Passerelle ${code.toUpperCase()} enregistrée avec succès.`);
        fetchGateways();
        setTimeout(() => setSuccessMsg(""), 4000);
      } else {
        setError(res.data?.message || "Erreur lors de l'enregistrement.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSavingCode(null);
    }
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '700', fontSize: '1.8rem' }}>
          <i className="bi bi-credit-card me-2"></i>
          Paramètres de paiement
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Tableau de bord</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Paiement NITA / AMANA</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4">
            {error}
            <button type="button" className="btn-close" onClick={() => setError("")}></button>
          </div>
        )}
        {successMsg && (
          <div className="alert alert-success alert-dismissible fade show mb-4">
            {successMsg}
            <button type="button" className="btn-close" onClick={() => setSuccessMsg("")}></button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: COLORS.primary }} role="status"></div>
          </div>
        ) : (
          <div className="row">
            {GATEWAYS.map((gateway) => (
              <GatewayCard
                key={gateway.code}
                gateway={gateway}
                data={passerelles[gateway.code]}
                onSave={handleSave}
                saving={savingCode === gateway.code}
              />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
};

export default ParametresPaiement;
